import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';
import './AudioPlayer.css';

const AudioPlayer = ({ audioUrl, audioDuration = 0 }) => {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(audioDuration);
  const [isLoaded, setIsLoaded] = useState(false);
  const [waveformData, setWaveformData] = useState([]);
  const [error, setError] = useState(null);

  // Update duration when prop changes
  useEffect(() => {
    if (audioDuration > 0) {
      setDuration(audioDuration);
    }
  }, [audioDuration]);

  // Generate random waveform data for visual representation
  const generateWaveformData = useCallback(() => {
    const bars = 50;
    const data = [];
    for (let i = 0; i < bars; i++) {
      const randomHeight = Math.random() * 0.6 + 0.2;
      data.push(randomHeight);
    }
    return data;
  }, []);

  // Draw the waveform
  const drawWaveform = useCallback((progress = 0) => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const barWidth = 4;
    const barGap = 3;
    const totalBars = waveformData.length;

    ctx.clearRect(0, 0, width, height);

    const progressIndex = Math.floor(progress * totalBars);

    waveformData.forEach((barHeight, index) => {
      const x = index * (barWidth + barGap);
      const barHeightPx = barHeight * height * 0.8;
      const y = (height - barHeightPx) / 2;

      if (index <= progressIndex) {
        ctx.fillStyle = '#5494DA';
      } else {
        ctx.fillStyle = '#A0D9FF';
      }

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeightPx, 2);
      ctx.fill();
    });
  }, [waveformData]);

  // Initialize audio and waveform
  useEffect(() => {
    if (!audioUrl) return;

    // Reset state for new audio
    setIsPlaying(false);
    setCurrentTime(0);
    setIsLoaded(false);
    setError(null);

    // Generate waveform data
    setWaveformData(generateWaveformData());

    // Clean up previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }

    // Create new audio element
    const audio = new Audio();
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      console.log('Audio loaded, duration:', audio.duration);
      if (audio.duration && audio.duration !== Infinity && !audioDuration) {
        setDuration(audio.duration);
      }
      setIsLoaded(true);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleCanPlay = () => {
      console.log('Audio can play');
      setIsLoaded(true);
    };

    const handleCanPlayThrough = () => {
      console.log('Audio can play through');
      setIsLoaded(true);
    };

    const handleDurationChange = () => {
      if (audio.duration && audio.duration !== Infinity && !audioDuration) {
        setDuration(audio.duration);
      }
    };

    const handleError = (e) => {
      console.error('Audio error:', e, audio.error);
      setError('Unable to play audio');
      setIsLoaded(true);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // Set source and load
    audio.src = audioUrl;
    audio.preload = 'auto';
    audio.load();

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.src = '';
    };
  }, [audioUrl, generateWaveformData, audioDuration]);

  // Draw waveform when data changes or progress changes
  useEffect(() => {
    if (waveformData.length > 0) {
      const progress = duration > 0 ? currentTime / duration : 0;
      drawWaveform(progress);
    }
  }, [waveformData, currentTime, duration, drawWaveform]);

  // Set canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const container = canvas.parentElement;
      canvas.width = container.offsetWidth;
      canvas.height = 60;
      drawWaveform(duration > 0 ? currentTime / duration : 0);
    }
  }, [drawWaveform, currentTime, duration]);

  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds && timeInSeconds !== 0) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        // Try to play
        await audio.play();
      }
    } catch (err) {
      console.error('Play error:', err);
      setError('Unable to play audio: ' + err.message);
    }
  };

  const handleCanvasClick = (e) => {
    const audio = audioRef.current;
    if (!audio || duration === 0) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = x / rect.width;
    audio.currentTime = progress * duration;
    setCurrentTime(audio.currentTime);
  };

  return (
    <div className="audio-player-container">
      <button
        className={`play-button ${isPlaying ? 'playing' : ''}`}
        onClick={handlePlayPause}
        disabled={isLoading}
      >
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      <div className="waveform-container">
        <canvas
          ref={canvasRef}
          className="waveform-canvas"
          onClick={handleCanvasClick}
        />
        <div className="time-display">
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="duration">{formatTime(duration)}</span>
        </div>
        {error && <div className="audio-error">{error}</div>}
      </div>
    </div>
  );
};

export default AudioPlayer;
